import type { Feature, FeatureCollection, MultiPolygon, Polygon, Position } from "geojson";
import { geometryHasCoordinates } from "@domain/Geo/geometry";
import { normalizePointsFC, type CoreCollection, type SamplePointCollection } from "@domain/Geo/sample";
import type { Job } from "../types";

/** The four editable geometry slices of a Job — boundary polygon, sample
 *  zones, sample points (normalised to SamplePointCollection), and cores.
 *  Any can be null when the job hasn't loaded that geometry. */
export interface JobGeoData {
	boundary: FeatureCollection | null;
	zones: FeatureCollection | null;
	points: SamplePointCollection | null;
	cores: CoreCollection | null;
}

/** Two color modes the JobMap renders in:
 *   - "boundary" — only the boundary polygon matters (other layers hidden);
 *     a job has geodata only if it has a boundary polygon geometry.
 *   - "standard" — any of boundary/points/zones counts as having geodata. */
export type JobMapColorMode = "boundary" | "standard";

/** Mission Control zone types that aren't field area. It rewrites `Bnd GeoJSON` from the INT mission
 *  one feature per zone (`name`), so these ride along with the field's own parts — `field`, plus
 *  `collected field` once a boundary is recorded (MC's boundary-approval layer draws exactly those
 *  two). Excluding the known nav/sampling zones, rather than keeping only `field`, fails safe: an
 *  unrecognized name still counts as field, and a nav zone never does. */
const NON_FIELD_ZONE_NAMES: ReadonlySet<string> = new Set([
	"unsafe",
	"slow",
	"pullin",
	"generic_zone",
	"sample_zone",
	"visual_zone",
]);

/**
 * Returns the boundary polygon (or multipolygon) for a job — every part of the field in
 * `job.files.boundaryGeoJSON`, combined, leaving out Mission Control's non-field zones (unsafe,
 * pull-in, slow, …; see {@link NON_FIELD_ZONE_NAMES}). Every other polygon counts: a customer file
 * names its parts freely or not at all. A single part comes back as-is. Returns null when the job
 * has no boundary file, no feature is a field polygon, or every geometry is empty (an integration
 * can hand back e.g. a MultiPolygon with `coordinates: []` — Agrian's empty boundary crashed the
 * wizard's map projection, which seeds bounds from `coordinates[0][0]…`).
 */
export function getJobBoundaryGeometry(job: Job): Polygon | MultiPolygon | null {
	const parts = (job.files?.boundaryGeoJSON?.features ?? []).filter(isFieldPolygon);
	if (parts.length === 1) return parts[0].geometry;
	return combineBoundaryPolygons(parts);
}

/** A polygon with coordinates that isn't one of Mission Control's non-field zones. */
function isFieldPolygon(feature: Feature | undefined): feature is Feature<Polygon | MultiPolygon> {
	const geom = feature?.geometry;
	if (!geom || (geom.type !== "Polygon" && geom.type !== "MultiPolygon") || !geometryHasCoordinates(geom)) {
		return false;
	}
	const name = feature?.properties?.name;
	return !(typeof name === "string" && NON_FIELD_ZONE_NAMES.has(name.toLowerCase()));
}

/**
 * Combines EVERY boundary polygon feature into one Polygon/MultiPolygon geometry
 * (not just the first). `Geo.merge` concatenates boundaries as one feature per
 * source job rather than unioning them, so this collapses that back to a single
 * geometry. Returns null when the features carry no polygon. Purely structural
 * (no turf union), so overlapping rings are kept as-is.
 *
 * Used by `mergeJobs` to dissolve a merged boundary into a single feature, so
 * every downstream consumer that reads only `features[0]` still draws the whole
 * shape, and by `getJobBoundaryGeometry` to join a multi-part field's parts.
 */
export function combineBoundaryPolygons(features: Feature[] | undefined): Polygon | MultiPolygon | null {
	if (!features?.length) return null;

	const polygons: Position[][][] = [];
	for (const feature of features) {
		const geom = feature.geometry;
		if (!geom) continue;
		if (geom.type === "Polygon") polygons.push(geom.coordinates);
		else if (geom.type === "MultiPolygon") polygons.push(...geom.coordinates);
	}

	if (polygons.length === 0) return null;
	if (polygons.length === 1) return { type: "Polygon", coordinates: polygons[0] };
	return { type: "MultiPolygon", coordinates: polygons };
}

/**
 * Predicate: does the job have any geodata to render?
 *
 * In "boundary" color mode the answer hinges only on the presence of a
 * boundary polygon — the only layer that mode draws.
 * In "standard" mode any of boundary / points / zones counts.
 */
export function jobHasGeodata(job: Job, mode: JobMapColorMode): boolean {
	if (mode === "boundary") return getJobBoundaryGeometry(job) !== null;
	return (
		(job.files?.boundaryGeoJSON?.features?.length ?? 0) > 0 ||
		(job.files?.pointsGeoJSON?.features?.length ?? 0) > 0 ||
		(job.files?.zonesGeoJSON?.features?.length ?? 0) > 0
	);
}

/**
 * Predicate: do none of the given jobs carry any boundary/zones/points
 * geometry? Used by the import submission paths to decide whether files are
 * required. Implemented in terms of `jobHasGeodata` so the definition of
 * "has geometry" stays in one place.
 */
export function allJobsHaveNoFiles(jobs: Job[]): boolean {
	return !jobs.some((job) => jobHasGeodata(job, "standard"));
}

/**
 * Pulls the four editable geometry slices off a Job and normalises the
 * points layer (so the GUI gets a consistent SamplePoint shape regardless
 * of source). Returns all-null when the job has no files at all.
 *
 * Used by the JobMap edit pipeline; reusable from any future surface that
 * needs the editable subset of a job's geometry (CLI exporters, sync logic).
 */
export function jobGeoData(job: Job): JobGeoData {
	const rawPoints = job.files?.pointsGeoJSON as FeatureCollection | undefined;
	return {
		boundary: (job.files?.boundaryGeoJSON as FeatureCollection) ?? null,
		zones: (job.files?.zonesGeoJSON as FeatureCollection) ?? null,
		points: rawPoints ? normalizePointsFC(rawPoints) : null,
		cores: null,
	};
}
